// pages/public/CategoryServicePage.jsx
import React, {
  useEffect,
  useState,
  useContext,
  useCallback,
  useMemo,
} from "react";
import { Link, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { serviceService } from "../../../services/api/serviceService";
import { AuthContext } from "../../../contexts/AuthContext";
import { toast } from "react-toastify";
import SearchBar from "./components/SearchBar";
import Loading from "../components/Loading";
import { removeVietnameseTones } from "../../../utils/RemoveVietnameseTone";
import Button from "../components/Button";

const formatServices = (arr) =>
  (arr || []).map((item) => ({
    id: item.id || item._id,
    name: item.serviceName || item.name,
    formName: item.formName,
    category: item.category?.categoryName,
    image: item?.image,
    submissionTime: item.submissionTime,
    reviewTime: item.reviewTime,
  }));

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { delayChildren: 0.05, staggerChildren: 0.03 },
  },
};
const itemVariants = {
  hidden: { opacity: 0, y: 10, scale: 0.98 },
  show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.35 } },
};

const CategoryServicePage = () => {
  const { id } = useParams();
  const { token_client } = useContext(AuthContext);
  const [services, setServices] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const fetchServices = useCallback(
    async (categoryId) => {
      try {
        setErr("");
        setLoading(true);
        const res = await serviceService.get_service_by_category(
          categoryId,
          token_client,
        );
        const formatted = formatServices(res?.services);
        setServices(formatted);
      } catch (e) {
        toast.dismiss();
        setErr("Không thể lấy danh sách dịch vụ.");
        toast.error("Không thể lấy danh sách dịch vụ.");
      } finally {
        setLoading(false);
      }
    },
    [token_client],
  );

  useEffect(() => {
    if (id) fetchServices(id);
  }, [id, fetchServices]);

  const filteredServices = useMemo(() => {
    const kw = removeVietnameseTones(searchText || "").trim();
    if (!kw) return services;
    return services.filter((s) =>
      removeVietnameseTones(s.name || "").includes(kw),
    );
  }, [searchText, services]);

  return (
    <div className="h-full w-full overflow-auto">
      <div className="mx-auto flex w-full flex-col items-center gap-8">
        <div className="w-full max-w-[911px] px-4">
          <SearchBar
            searchText={searchText}
            setSearchText={setSearchText}
            onSearch={() => {}}
            placeholder="Tìm dịch vụ..."
          />
        </div>

        {loading ? (
          <div className="flex justify-center py-10">
            <Loading />
          </div>
        ) : err ? (
          <div className="flex flex-col items-center gap-3 py-10">
            <img
              src="/images/empty.png"
              alt="Lỗi tải dữ liệu"
              className="size-25 opacity-70"
              loading="lazy"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/images/empty.png";
              }}
            />
            <p className="text-sm text-gray-600">{err}</p>
            <Button
              type="button"
              variant="primary"
              size="lg"
              onClick={() => fetchServices(id)}
            >
              Thử lại
            </Button>
          </div>
        ) : filteredServices.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-10">
            <img
              src="/images/empty.png"
              alt="Không có dịch vụ"
              className="size-25 opacity-70"
              loading="lazy"
            />
            <p className="text-sm text-gray-600">
              Không có dịch vụ nào phù hợp với từ khóa{" "}
              <b>&ldquo;{searchText}&rdquo;</b>.
            </p>
          </div>
        ) : (
          <AnimatePresence mode="popLayout">
            <motion.ul
              role="list"
              initial="hidden"
              animate="show"
              variants={containerVariants}
              className="
                grid w-full gap-4 sm:gap-5
                grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5
              "
            >
              {filteredServices?.map((s) => (
                <motion.li
                  role="listitem"
                  key={s.id}
                  variants={itemVariants}
                  layout
                >
                  <Link
                    to={`/danh-sach-dich-vu/dich-vu/${s.formName}/${s.id}`}
                    className="
                      flex h-full w-full flex-col items-center justify-between gap-4 rounded-xl
                      border border-[#0457c269] bg-white p-4
                      shadow-sm transition-all duration-200 hover:-translate-y-[2px] hover:shadow-lg
                      outline-none focus:border-[#7DB5FF] focus:ring-2 focus:ring-[#0155F8]/25 focus:ring-offset-2
                      focus-visible:border-[#7DB5FF] focus-visible:ring-2 focus-visible:ring-[#0155F8]/25 focus-visible:ring-offset-2
                      active:border-[#7DB5FF] active:ring-2 active:ring-[#0155F8]/20
                    "
                    aria-label={`Mở dịch vụ ${s.name}`}
                  >
                    <div className="flex flex-col items-center justify-center gap-4 text-center">
                      <img
                        src={s?.image || "/images/document-sign.png"}
                        alt=""
                        className="h-16 w-16 object-contain object-center shrink-0"
                        loading="lazy"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "/images/document-sign.png";
                        }}
                      />
                      <p className="line-clamp-2 text-[15px] font-medium text-[#0457C2]">
                        {s.name}
                      </p>
                      {s?.category && (
                        <p className="text-xs italic text-gray-500">
                          {s.category}
                        </p>
                      )}
                      <div className="space-y-1 text-left text-xs text-gray-600 md:hidden">
                        <p>
                          <span className="font-medium text-gray-700">
                            Nộp đơn:
                          </span>{" "}
                          {s.submissionTime || "--"}
                        </p>
                        <p>
                          <span className="font-medium text-gray-700">
                            Thẩm định:
                          </span>{" "}
                          {s.reviewTime || "--"}
                        </p>
                      </div>
                    </div>

                    <div className="flex w-full flex-col items-center justify-between gap-2 2xl:flex-row">
                      <div className="flex items-center gap-1 text-xs text-gray-700">
                        <img
                          src="/icons/clock.svg"
                          alt=""
                          className="h-3 w-3"
                        />
                        <span className="whitespace-nowrap">
                          4–5 ngày làm việc
                        </span>
                      </div>
                      <Button
                        type="button"
                        variant="primary"
                        rightIcon={
                          <img
                            src="/icons/plus.svg"
                            alt=""
                            className="h-4 w-4 shrink-0"
                          />
                        }
                      >
                        Tạo ngay
                      </Button>
                    </div>
                  </Link>
                </motion.li>
              ))}
            </motion.ul>
          </AnimatePresence>
        )}
      </div>
    </div>
  );
};

export default CategoryServicePage;
